import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { stripMarkdown } from '@/lib/markdown-utils';
import { AnchorHeading } from '@/components/docs/AnchorHeading';
import { CodeBlock } from '@/components/docs/CodeBlock';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Generate heading IDs for TOC support */
  generateHeadingIds?: boolean;
  /** Use compact prose sizing (prose-sm) */
  compact?: boolean;
}

export function MarkdownRenderer({ 
  content, 
  className,
  generateHeadingIds = false,
  compact = false,
}: MarkdownRendererProps) {
  return (
    <div className={cn(
      'prose dark:prose-invert max-w-none',
      compact ? 'prose-sm' : 'prose-lg',
      className
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-10 mb-4">{children}</h1>
          ),
          h2: ({ children }) => {
            const text = stripMarkdown(String(children));
            const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            
            if (generateHeadingIds) {
              return (
                <AnchorHeading level={2} id={id} className="mt-6 md:mt-8 mb-3 md:mb-4">
                  {children}
                </AnchorHeading>
              );
            }
            return (
              <h2 id={id} className="text-xl md:text-2xl font-semibold mt-6 md:mt-8 mb-3 md:mb-4 scroll-mt-24">
                {children}
              </h2>
            );
          },
          h3: ({ children }) => {
            const text = stripMarkdown(String(children));
            const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            
            if (generateHeadingIds) {
              return (
                <AnchorHeading level={3} id={id} className="mt-5 md:mt-6 mb-2 md:mb-3">
                  {children}
                </AnchorHeading>
              );
            }
            return (
              <h3 id={id} className="text-lg md:text-xl font-semibold mt-5 md:mt-6 mb-2 md:mb-3 scroll-mt-24">
                {children}
              </h3>
            );
          },
          p: ({ children }) => (
            <p className="mb-4 leading-relaxed text-foreground/90">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="text-foreground/90">{children}</li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/30 pl-4 italic my-6 text-muted-foreground">
              {children}
            </blockquote>
          ),
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
                  {children}
                </code>
              );
            }
            // Fenced code blocks are handled by <pre>
            return <>{children}</>;
          },
          pre: ({ children }) => {
            // Extract language from code element's className (e.g., "language-typescript")
            const codeElement = children as React.ReactElement;
            const className = codeElement?.props?.className || '';
            const match = className.match(/language-(\w+)/);
            const language = match ? match[1] : 'plaintext';
            const code = String(codeElement?.props?.children || '').replace(/\n$/, '');
            
            return (
              <div className="my-4">
                <CodeBlock
                  code={code}
                  language={language}
                  showLineNumbers
                />
              </div>
            );
          },
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="text-primary underline hover:text-primary/80"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse border border-border text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody>
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-border">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 text-left font-semibold border-r border-border last:border-r-0">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 border-r border-border last:border-r-0">
              {children}
            </td>
          ),
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt || ''}
              className="rounded-lg max-w-full h-auto my-6"
              loading="lazy"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
